import { type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CurrencyCodeEnum } from '@electricjs/core_entity-client';

import {
  StorefrontCartProvider,
  StorefrontCheckoutFormProvider,
  StorefrontStepProvider,
  OtherCountryFlowProvider,
  useStorefrontCart,
  useStorefrontCheckoutForm,
  useStorefrontStep,
  useOtherCountryFlow,
} from './components';

import { SelectionParamKeys } from '@/types/queryParamKeys';
import { StorefrontSteps } from '@/types/storefront';
import { SchemaOption } from './components';

type StorefrontProviderProps = {
  children: ReactNode;
};

type StorefrontResetOptions = {
  keepQueryParams: boolean;
};

/**
 * The Storefront provider for its global state (carts, checkout data, etc)
 */
export const StorefrontProvider = ({ children }: StorefrontProviderProps) => {
  return (
    <StorefrontCartProvider>
      <StorefrontCheckoutFormProvider>
        <StorefrontStepProvider>
          <OtherCountryFlowProvider>{children}</OtherCountryFlowProvider>
        </StorefrontStepProvider>
      </StorefrontCheckoutFormProvider>
    </StorefrontCartProvider>
  );
};

export const useStorefront = () => {
  const storefrontCartValue = useStorefrontCart();
  const storefrontCheckoutValue = useStorefrontCheckoutForm();
  const storefrontStepValue = useStorefrontStep();
  const useOtherCountryFlowValue = useOtherCountryFlow();

  const [queryParams, writeQueryParams] = useSearchParams();

  const employeeId = queryParams.get(SelectionParamKeys.EmployeeId);

  const resetStorefront = (options?: StorefrontResetOptions) => {
    const {
      setAssetCart,
      setCatalogCart,
      setCatalogShippingCost,
      setWarehouseShippingCost,
      setOrderTotal,
      setTodayTotal,
      setMonthlyInstallment,
      setOrderCurrency,
    } = storefrontCartValue;
    const { storefrontCheckoutForm, setSchemaOption } = storefrontCheckoutValue;
    const { storefrontStep, setStorefrontStep, setConfirmedOrder } =
      storefrontStepValue;
    const {
      otherCountryFlowFirstStepForm,
      otherCountryFlowSecondStepForm,
      setOtherCountryFlowStep,
    } = useOtherCountryFlowValue;

    // reset all storefront values
    setAssetCart([]);
    setCatalogCart([]);
    setCatalogShippingCost(undefined);
    setWarehouseShippingCost(undefined);
    setOrderTotal(0);
    setTodayTotal(0);
    setMonthlyInstallment(0);
    setOrderCurrency(CurrencyCodeEnum.Usd);
    storefrontCheckoutForm?.reset();
    setConfirmedOrder(undefined);
    otherCountryFlowFirstStepForm?.reset();
    otherCountryFlowSecondStepForm?.reset();
    setOtherCountryFlowStep(1);

    // clear query params (e.g., employeeId)
    if (!options?.keepQueryParams && employeeId) {
      queryParams.delete(SelectionParamKeys.EmployeeId);
      writeQueryParams(queryParams, {
        // Each change in the query params will replace the current history state, meaning the "back button" will go back to the previous page.
        replace: true,
      });
    }

    // go back to first step
    if (storefrontStep !== StorefrontSteps.SHOPPING) {
      setStorefrontStep(StorefrontSteps.SHOPPING);
      setSchemaOption(SchemaOption.DEFAULT);
    }
  };

  /** Clear all carts of storefront (Asset, Catalog, otherCountryFlow) */
  const clearAllCarts = () => {
    const { setAssetCart, setCatalogCart } = storefrontCartValue;
    const { otherCountryFlowSecondStepForm } = useOtherCountryFlowValue;

    setAssetCart([]);
    setCatalogCart([]);
    otherCountryFlowSecondStepForm?.resetField('selectedHardware');
  };

  return {
    ...storefrontCartValue,
    ...storefrontCheckoutValue,
    ...storefrontStepValue,
    ...useOtherCountryFlowValue,
    resetStorefront,
    clearAllCarts,
  };
};
